import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listPeople, listHappenings, deleteMedia, updatePerson, updateHappening, type Person, type Happening } from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ViewToggle } from '@/components/ViewToggle';
import { SearchFilter } from '@/components/SearchFilter';
import { useViewStore } from '@/lib/viewStore';
import { useAuth } from '@/contexts/AuthContext';
import { Trash2, Image, User, Calendar, ExternalLink } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

type MediaItem = {
    id: string;
    url: string;
    entityType: 'person' | 'happening';
    entityId: string;
    entityName: string;
    entitySlug?: string;
    uploadedAt?: string;
};

export default function MediaManagerPage() {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const viewMode = useViewStore((state) => state.getViewMode('media'));
    const setViewMode = useViewStore((state) => state.setViewMode);
    
    const { user } = useAuth();
    const canDelete = !user || user.role === 'owner' || user.role === 'admin';
    
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingMedia, setDeletingMedia] = useState<MediaItem | null>(null);
    const [checkingDeletion, setCheckingDeletion] = useState(false);

    const { data: people = [] } = useQuery({
        queryKey: ['people'],
        queryFn: () => listPeople({}),
    });

    const { data: happenings = [] } = useQuery({
        queryKey: ['happenings'],
        queryFn: () => listHappenings(),
    });

    // Collect all media items from both database references and storage bucket
    const mediaItems = useMemo<MediaItem[]>(() => {
        const items: MediaItem[] = [];
        const urlToEntity = new Map<string, { type: 'person' | 'happening'; id: string; name: string; slug?: string }>();
        
        // Map database-referenced media
        people.forEach((person: Person) => {
            if (person.picture_url) {
                urlToEntity.set(person.picture_url, {
                    type: 'person',
                    id: person.id,
                    name: person.name,
                    slug: person.slug,
                });
            }
        });
        
        happenings.forEach((happening: Happening) => {
            if (happening.image_url) {
                urlToEntity.set(happening.image_url, {
                    type: 'happening',
                    id: happening.id,
                    name: happening.title || happening.description?.slice(0, 50) || 'Untitled',
                    slug: happening.slug,
                });
            }
        });
        
        // Add files from database references first
        urlToEntity.forEach((entity, url) => {
            items.push({
                id: `${entity.type}-${entity.id}`,
                url: url,
                entityType: entity.type,
                entityId: entity.id,
                entityName: entity.name,
                entitySlug: entity.slug,
            });
        });
        
        // Remove duplicates (in case a file appears multiple times)
        const seen = new Set<string>();
        return items.filter((item) => {
            if (seen.has(item.url)) {
                return false;
            }
            seen.add(item.url);
            return true;
        });
    }, [people, happenings]);

    // Filter media based on search
    const filteredMedia = useMemo(() => {
        if (!searchQuery.trim()) return mediaItems;
        const lowerQuery = searchQuery.toLowerCase();
        return mediaItems.filter(
            (item) =>
                item.entityName.toLowerCase().includes(lowerQuery) ||
                item.entitySlug?.toLowerCase().includes(lowerQuery) ||
                item.url.toLowerCase().includes(lowerQuery)
        );
    }, [mediaItems, searchQuery]);

    // Delete mutation
    const deleteMediaMutation = useMutation({
        mutationFn: async ({ url, entityType, entityId }: { url: string; entityType: 'person' | 'happening'; entityId: string }) => {
            // Delete the media file
            await deleteMedia(url);
            
            // Update the entity to remove the media URL (only if entityId is provided)
            if (entityId) {
                if (entityType === 'person') {
                    await updatePerson(entityId, { picture_url: null });
                } else {
                    await updateHappening(entityId, { image_url: null });
                }
            }
            // For orphaned files (no entityId), we just delete from storage
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['people'] });
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            setDeleteDialogOpen(false);
            setDeletingMedia(null);
        },
    });

    const handleDeleteClick = (item: MediaItem) => {
        setDeletingMedia(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (deletingMedia) {
            deleteMediaMutation.mutate({
                url: deletingMedia.url,
                entityType: deletingMedia.entityType,
                entityId: deletingMedia.entityId,
            });
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-3xl font-bold">Media Manager</h1>
                <p className="text-muted-foreground">
                    View and manage uploaded photos for people and happenings
                </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
                <SearchFilter
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search by entity name, slug, or URL..."
                    className="w-full max-w-sm"
                />
                <ViewToggle
                    view={viewMode}
                    onViewChange={(mode) => setViewMode('media', mode)}
                    availableViews={['list']}
                />
                {searchQuery && (
                    <span className="text-sm text-muted-foreground">
                        {filteredMedia.length} of {mediaItems.length} media files
                    </span>
                )}
            </div>

            {filteredMedia.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Image className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">
                            {searchQuery ? 'No media files found matching your search.' : 'No media files uploaded yet.'}
                        </p>
                    </CardContent>
                </Card>
            ) : viewMode === 'card' ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredMedia.map((item) => (
                        <Card key={item.id} className="relative">
                            {canDelete && (
                                <div className="absolute top-2 right-2 z-10">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteClick(item)}
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        title="Delete media"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                            <CardHeader>
                                <div className="flex items-start gap-2">
                                    {item.entityType === 'person' ? (
                                        <User className="h-4 w-4 text-muted-foreground mt-1" />
                                    ) : (
                                        <Calendar className="h-4 w-4 text-muted-foreground mt-1" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <CardTitle className="text-base truncate">{item.entityName}</CardTitle>
                                        <CardDescription className="text-xs capitalize">
                                            {item.entityType} {item.entitySlug && `• ${item.entitySlug}`}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-hidden rounded-md border mb-2">
                                    <img
                                        src={item.url}
                                        alt={`${item.entityName} media`}
                                        className="h-48 w-full object-cover"
                                        loading="lazy"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EFailed to load%3C/text%3E%3C/svg%3E';
                                        }}
                                    />
                                </div>
                                <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-primary hover:underline flex items-center gap-1"
                                >
                                    <ExternalLink className="h-3 w-3" />
                                    View original
                                </a>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Preview</TableHead>
                                <TableHead>Entity</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>URL</TableHead>
                                {canDelete && <TableHead className="w-12">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredMedia.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <div className="h-16 w-16 overflow-hidden rounded border">
                                            <img
                                                src={item.url}
                                                alt={item.entityName}
                                                className="h-full w-full object-cover"
                                                loading="lazy"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23ddd" width="64" height="64"/%3E%3C/svg%3E';
                                                }}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        <div>
                                            <p>{item.entityName}</p>
                                            {item.entitySlug && (
                                                <p className="text-xs text-muted-foreground">{item.entitySlug}</p>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center gap-1 text-xs capitalize">
                                            {item.entityType === 'person' ? (
                                                <>
                                                    <User className="h-3 w-3" />
                                                    Person
                                                </>
                                            ) : (
                                                <>
                                                    <Calendar className="h-3 w-3" />
                                                    Happening
                                                </>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <a
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-primary hover:underline flex items-center gap-1 max-w-xs truncate"
                                        >
                                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">{item.url}</span>
                                        </a>
                                    </TableCell>
                                    {canDelete && (
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteClick(item)}
                                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                                title="Delete media"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deletingMedia && (
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <DialogContent size="large">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Trash2 className="h-5 w-5 text-destructive" />
                                Delete Media?
                            </DialogTitle>
                            <DialogDescription>
                                {deletingMedia.entityId
                                    ? `This will remove the photo from ${deletingMedia.entityType === 'person' ? 'this person' : 'this happening'}. The file will be deleted from storage.`
                                    : 'This orphaned file will be permanently deleted from storage.'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 px-4 sm:px-6 py-4">
                            {deletingMedia.entityId && (
                                <div className="rounded-lg border p-4">
                                    <div className="mb-2 text-sm font-medium">Entity:</div>
                                    <div className="text-sm text-muted-foreground">{deletingMedia.entityName}</div>
                                </div>
                            )}
                            <div className="overflow-hidden rounded-md border">
                                <img
                                    src={deletingMedia.url}
                                    alt="Preview"
                                    className="h-48 w-full object-cover"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setDeleteDialogOpen(false)}
                                disabled={deleteMediaMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={handleDeleteConfirm}
                                disabled={deleteMediaMutation.isPending}
                            >
                                {deleteMediaMutation.isPending ? (
                                    'Deleting...'
                                ) : (
                                    <>
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Delete Media
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
